import type { Basemap } from '../map/mapInit'
import { selectBasemap } from '../map/mapInit'
import { layerInfoOf, type WmsTileLayer } from '../map/layers'
import { displayName, displayNameText, groupLayers, type LayerGroup } from '../map/layerNames'
import type { VectorModeController } from '../map/vectorMode'
import { isLabelSupported, type LabelController } from '../map/layerLabels'
import { hideAttributesDrawer, showAttributesDrawer } from './attributesDrawer'
import { onDataTableClose } from './dataTable'
import type { Extent } from 'ol/extent'

/**
 * Layer panel (PRD P0.6): exactly the advertised layers, with on/off toggles,
 * opacity sliders, and a GetLegendGraphic legend per visible layer. Shapes and
 * Attributes are visible for every map-viewer plan; the proxy remains the
 * authority for whether the backing WFS request is accepted.
 */
export function renderLayerPanel(
  panel: HTMLElement,
  wmsLayers: WmsTileLayer[],
  basemaps: Basemap[],
  vector: VectorModeController | null = null,
  labels: LabelController | null = null,
  onZoomTo?: (extent: Extent) => void,
): void {
  panel.innerHTML = `<h2 class="panel-title">Layers</h2>`

  // Grouped by theme, not by workspace. The old sub-heading keyed on
  // `workspace` and only appeared when there was more than one — every
  // production layer is in `mining`, so it never appeared at all and the list
  // was flat however long it grew.
  const sections = groupLayers(wmsLayers, (layer) => {
    const info = layerInfoOf(layer)
    return { name: info.name, title: info.title }
  })

  sections.forEach(({ group, items }, index) => {
    panel.appendChild(
      groupSection(group, index + 1, items, vector, labels, onZoomTo),
    )
  })

  if (wmsLayers.length === 0) {
    const empty = document.createElement('p')
    empty.className = 'panel-empty'
    empty.textContent = 'No layers are available for your subscription.'
    panel.appendChild(empty)
  }

  panel.appendChild(basemapPicker(basemaps))
}

/**
 * One collapsible section: a numbered heading, a count, and the rows.
 *
 * The heading is a real <button> with `aria-expanded`, not a styled div — the
 * panel has to be operable from the keyboard, and a div is not.
 *
 * Collapse state is remembered per group. Zoomed into one province a customer
 * may care only about claims; reopening five sections on every reload is the
 * kind of small tax that makes a tool feel heavy.
 */
function groupSection(
  group: LayerGroup | null,
  index: number,
  layers: WmsTileLayer[],
  vector: VectorModeController | null,
  labels: LabelController | null,
  onZoomTo?: (extent: Extent) => void,
): HTMLElement {
  const section = document.createElement('section')
  section.className = 'layer-group'
  // `null` is a layer GeoServer serves that the registry has never heard of.
  // It renders under "Other" rather than vanishing — see `layerGroup()`.
  section.dataset.layerGroup = group ?? 'other'
  const groupKey = section.dataset.layerGroup

  // The title row used to be a single <button> holding the index, name,
  // count and chevron. A button inside a button is invalid HTML, so the eye
  // (added next) needs a sibling of its own: the row becomes a plain
  // container with the title button — unchanged behaviour, unchanged
  // classes — and the count as a second sibling, so the eye can sit between
  // them without touching either.
  const head = document.createElement('div')
  head.className = 'layer-group__head'

  const heading = document.createElement('button')
  heading.type = 'button'
  heading.className = 'layer-group__heading'
  heading.innerHTML =
    `<span class="layer-group__index">${String(index).padStart(2, '0')}</span>` +
    `<span class="layer-group__name">${group ?? 'Other'}</span>` +
    `<span class="layer-group__chevron" aria-hidden="true"></span>`

  const eyeBtn = document.createElement('button')
  eyeBtn.type = 'button'
  eyeBtn.className = 'layer-group__eye'

  const count = document.createElement('span')
  count.className = 'layer-group__count'
  count.textContent = String(layers.length)

  head.append(heading, eyeBtn, count)

  const body = document.createElement('div')
  body.className = 'layer-group__body'
  for (const layer of layers) body.appendChild(layerRow(layer, vector, labels, onZoomTo))

  const apply = (collapsed: boolean) => {
    section.classList.toggle('layer-group--collapsed', collapsed)
    heading.setAttribute('aria-expanded', String(!collapsed))
    body.hidden = collapsed
  }
  apply(isCollapsed(groupKey))

  heading.addEventListener('click', () => {
    const next = !section.classList.contains('layer-group--collapsed')
    apply(next)
    setCollapsed(groupKey, next)
  })

  wireGroupEye(eyeBtn, body, groupKey, group ?? 'Other')

  section.append(head, body)
  return section
}

/** Inline SVGs (no icon library for two drawings) — no visible text; the
 * button's accessible name comes entirely from `aria-label`. */
const EYE_OPEN_ICON =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<path d="M1 8s2.7-5 7-5 7 5 7 5-2.7 5-7 5-7-5-7-5Z" fill="none" stroke="currentColor" stroke-width="1.3"/>' +
  '<circle cx="8" cy="8" r="2.1" fill="currentColor"/></svg>'

const EYE_CLOSED_ICON =
  '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<path d="M1 8s2.7-5 7-5 7 5 7 5-2.7 5-7 5-7-5-7-5Z" fill="none" stroke="currentColor" stroke-width="1.3"/>' +
  '<circle cx="8" cy="8" r="2.1" fill="currentColor"/>' +
  '<line x1="1.5" y1="1.5" x2="14.5" y2="14.5" stroke="currentColor" stroke-width="1.4"/></svg>'

/**
 * The eye is a second, independent path to the same visibility switch every
 * row's checkbox already owns (PRD §4.1): it only checks/unchecks boxes and
 * replays their `change` event, so `layerRow()`'s handler stays the single
 * place that talks to OpenLayers and the labels. Its own state is never
 * stored — it is recomputed from the checkboxes on every change, including
 * the ones the eye itself triggers (guarded by `applyingByEye` so a replayed
 * `change` isn't mistaken for a manual one).
 *
 * Restoring recalls the selection that was showing right before the hide
 * that memorized it (`gv.layerGroups.hidden`, read only on this click); with
 * nothing recognizable remembered, every layer comes back instead.
 */
function wireGroupEye(
  eyeBtn: HTMLButtonElement,
  body: HTMLElement,
  groupKey: string,
  groupLabel: string,
): void {
  let applyingByEye = false

  const rows = (): HTMLElement[] => Array.from(body.querySelectorAll('.layer-row'))
  const checkboxOf = (row: HTMLElement): HTMLInputElement =>
    row.querySelector('input[type="checkbox"]') as HTMLInputElement
  const isHidden = (): boolean => rows().every((row) => !checkboxOf(row).checked)

  const updateState = (): void => {
    const hidden = isHidden()
    eyeBtn.setAttribute('aria-pressed', String(hidden))
    eyeBtn.setAttribute(
      'aria-label',
      hidden ? `Show ${groupLabel} layers` : `Hide all ${groupLabel} layers`,
    )
    eyeBtn.innerHTML = hidden ? EYE_CLOSED_ICON : EYE_OPEN_ICON
    eyeBtn.classList.toggle('layer-group__eye--hidden', hidden)
  }
  // Rows are built before this runs, and their checkboxes already reflect
  // whatever a deep link or the saved view state set (`main.ts` calls
  // `layer.setVisible()` before `renderLayerPanel()`), so this reads the
  // real initial state rather than a default.
  updateState()

  const setChecked = (row: HTMLElement, checked: boolean): void => {
    const checkbox = checkboxOf(row)
    if (checkbox.checked === checked) return
    checkbox.checked = checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  }

  eyeBtn.addEventListener('click', () => {
    const currentRows = rows()
    applyingByEye = true
    if (isHidden()) {
      const toShow = new Set(recalledSelection(groupKey, currentRows))
      for (const row of currentRows) setChecked(row, toShow.has(row.dataset.layerName ?? ''))
      // The memory only ever describes a group the eye itself hid. Once it
      // is spent, drop it: otherwise a group later unchecked *by hand* would
      // recall this old selection instead of everything (PRD §3.2, AC 4).
      forgetHidden(groupKey)
    } else {
      const checkedNames = currentRows
        .filter((row) => checkboxOf(row).checked)
        .map((row) => row.dataset.layerName ?? '')
      rememberHidden(groupKey, checkedNames)
      for (const row of currentRows) setChecked(row, false)
    }
    applyingByEye = false
    updateState()
  })

  // A manual check/uncheck bubbles up here too (both the eye's own replayed
  // `change` and a real click land on the same listener — `applyingByEye`
  // tells them apart). §3.4: checking a box by hand while the group was
  // fully hidden reopens the eye *and* drops the memorized selection, so the
  // next hide starts clean instead of resurrecting an old, unrelated set —
  // the next hide (if any) overwrites the memory anyway, but a reload in
  // between must not restore stale layers this click never asked for.
  body.addEventListener('change', (event) => {
    if (applyingByEye) return
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return
    if (target.checked) {
      const others = rows().filter((row) => checkboxOf(row) !== target)
      if (others.every((row) => !checkboxOf(row).checked)) forgetHidden(groupKey)
    }
    updateState()
  })
}

const HIDDEN_KEY = 'gv.layerGroups.hidden'

function hiddenMemory(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    // Anything but a plain object (a tampered or foreign value) is ignored,
    // so the write paths can index into it without a TypeError outside try.
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, string[]>)
      : {}
  } catch {
    return {} // storage unavailable or unreadable — the eye still works for this session
  }
}

function rememberHidden(group: string, layerNames: string[]): void {
  const memory = hiddenMemory()
  memory[group] = layerNames
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(memory))
  } catch {
    /* the eye still works for this session */
  }
}

function forgetHidden(group: string): void {
  const memory = hiddenMemory()
  if (!(group in memory)) return
  delete memory[group]
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(memory))
  } catch {
    /* the eye still works for this session */
  }
}

/** Memory is read only when the eye is clicked to restore (§3.5). Names no
 * longer in the group are dropped; if nothing recognizable is left, every
 * layer is checked instead (§3.2's empty-memory fallback). */
function recalledSelection(group: string, rows: HTMLElement[]): string[] {
  const remembered = hiddenMemory()[group] ?? []
  const names = new Set(rows.map((row) => row.dataset.layerName))
  const known = remembered.filter((name) => names.has(name))
  return known.length > 0 ? known : rows.map((row) => row.dataset.layerName ?? '')
}

const COLLAPSE_KEY = 'gv.layerGroups.collapsed'

function collapsedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set() // storage unavailable — everything opens, which is the safe default
  }
}

function isCollapsed(group: string): boolean {
  return collapsedSet().has(group)
}

function setCollapsed(group: string, collapsed: boolean): void {
  const set = collapsedSet()
  if (collapsed) set.add(group)
  else set.delete(group)
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...set]))
  } catch {
    /* the section still collapses for this session */
  }
}

function layerRow(
  layer: WmsTileLayer,
  vector: VectorModeController | null,
  labels: LabelController | null,
  onZoomTo?: (extent: Extent) => void,
): HTMLElement {
  const info = layerInfoOf(layer)
  const row = document.createElement('div')
  row.className = 'layer-row'
  row.dataset.layerName = info.name

  const label = document.createElement('label')
  label.className = 'layer-toggle'
  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.checked = layer.getVisible()
  const disp = displayName(info.name, info.title)
  const title = document.createElement('span')
  title.textContent = disp.label
  if (disp.sub) {
    const sub = document.createElement('i')
    sub.className = 'layer-name__sub'
    sub.textContent = ` ${disp.sub}`
    title.append(sub)
  }
  if (info.abstract) title.title = info.abstract
  label.append(checkbox, title)

  // Opacity: a thin track filled up to the current value, with the value
  // written beside it. The bare range slider said nothing — a customer could
  // see roughly where the handle sat and not what they had actually set, which
  // matters when the answer is going into a screenshot for a report.
  const opacityRow = document.createElement('div')
  opacityRow.className = 'layer-opacity-row'

  const opacity = document.createElement('input')
  opacity.type = 'range'
  opacity.className = 'layer-opacity' // e2e hook — do not rename
  opacity.min = '0'
  opacity.max = '100'
  opacity.value = String(Math.round(layer.getOpacity() * 100))
  opacity.title = 'Opacity'
  opacity.setAttribute('aria-label', `${displayNameText(info.name, info.title)} opacity`)

  const opacityValue = document.createElement('span')
  opacityValue.className = 'layer-opacity__value'

  // The readout and the gold fill are the same number, written once. Always a
  // rounded integer: `setOpacity` takes a float and 0.7300000000000001 is a
  // real value for it to hold.
  const paintOpacity = () => {
    const percent = Math.round(Number(opacity.value))
    opacity.style.setProperty('--fill', `${percent}%`)
    opacityValue.textContent = `${percent}%`
  }
  paintOpacity()
  opacityRow.append(opacity, opacityValue)

  const legend = document.createElement('div')
  legend.className = 'layer-legend'
  const legendImg = document.createElement('img')
  legendImg.alt = `${displayNameText(info.name, info.title)} legend`
  legendImg.loading = 'lazy'
  legend.appendChild(legendImg)
  const layerTools: HTMLElement[] = []

  // Opacity, legend and per-layer tools belong to a layer you can actually see.
  // Controls under an unticked layer invite a change with no visible effect.
  const syncVisibility = () => {
    const visible = layer.getVisible()
    legend.hidden = !visible
    opacityRow.hidden = !visible
    for (const tool of layerTools) tool.hidden = !visible
    if (visible && !legendImg.src) legendImg.src = info.legendUrl
  }
  checkbox.addEventListener('change', () => {
    if (vector) vector.setVisible(layer, checkbox.checked)
    else layer.setVisible(checkbox.checked)
    labels?.setVisible(layer, checkbox.checked)
    syncVisibility()
  })
  opacity.addEventListener('input', () => {
    layer.setOpacity(Math.round(Number(opacity.value)) / 100)
    paintOpacity()
  })
  legendImg.addEventListener('error', () => {
    legend.hidden = true
  })
  syncVisibility()

  row.append(label, opacityRow)

  // Three switches with one grammar (T3): each is named after what it turns on,
  // none renames itself, none appears or disappears. What is pressed is the
  // state — no need to decode whether a label means "current" or "destination".
  if (vector) {
    // "Shapes", not "Vector" (T6): the label says what the user sees change —
    // a map picture becomes the shapes themselves. GIS vocabulary stays out of
    // the interface. The CSS classes keep their old names on purpose: they are
    // the e2e hooks, and renaming them would add noise for nothing.
    const modeBtn = switchButton('layer-mode', 'Shapes', 'Draw the actual shapes instead of a map picture')
    const attrsBtn = switchButton('layer-attrs', 'Attributes', 'Show this layer’s data in the table below')

    modeBtn.addEventListener('click', () => {
      setPressed(modeBtn, vector.toggle(layer), 'layer-mode--vector')
    })

    attrsBtn.addEventListener('click', () => {
      if (attrsBtn.getAttribute('aria-pressed') === 'true') {
        hideAttributesDrawer() // onDataTableClose un-presses the button
        return
      }
      // Attributes no longer imply vector rendering: this only starts the WFS
      // load behind the scenes (vectorMode keeps the twin unpainted).
      vector.setAttributes(layer, true)
      showAttributesDrawer({
        key: info.name,
        title: displayNameText(info.name, info.title),
        features: () => vector.featuresOf(layer),
        onZoomTo,
      })
      setPressed(attrsBtn, true, 'layer-attrs--on')
    })

    // Single source of truth for the pressed state: the table announces whoever
    // stopped being displayed — closed by its ×, or evicted by another layer.
    onDataTableClose((key) => {
      if (key !== info.name) return
      setPressed(attrsBtn, false, 'layer-attrs--on')
      vector.setAttributes(layer, false)
    })

    layerTools.push(modeBtn, attrsBtn)
    row.append(modeBtn, attrsBtn)
  }

  if (labels && isLabelSupported(info.name)) {
    const labelsBtn = switchButton('layer-labels', 'Labels', 'Toggle labels on this layer')
    labelsBtn.addEventListener('click', () => {
      setPressed(labelsBtn, labels.toggle(layer), 'layer-labels--on')
    })
    layerTools.push(labelsBtn)
    row.append(labelsBtn)
  }

  syncVisibility()
  row.append(legend)
  return row
}

/** One of the row's uniform on/off switches. */
function switchButton(className: string, text: string, hint: string): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = className
  button.textContent = text
  button.title = hint
  button.setAttribute('aria-pressed', 'false')
  return button
}

function setPressed(button: HTMLButtonElement, on: boolean, onClass: string): void {
  button.setAttribute('aria-pressed', String(on))
  button.classList.toggle(onClass, on)
}

function basemapPicker(basemaps: Basemap[]): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'basemap-picker'
  const heading = document.createElement('h3')
  heading.textContent = 'Basemap'
  const select = document.createElement('select')
  select.setAttribute('aria-label', 'Basemap')
  for (const bm of basemaps) {
    const option = document.createElement('option')
    option.value = bm.id
    option.textContent = bm.title
    option.selected = bm.layer?.getVisible() ?? false
    select.appendChild(option)
  }
  select.addEventListener('change', () => selectBasemap(basemaps, select.value))
  wrap.append(heading, select)
  return wrap
}
